// 港湾・チョークポイントの時系列から「極端な増減（異常）」を検知する。
// FRED モニターの change20d()（変化率）と sigma()（Zスコア）の考え方を、
// AIS 由来で非常にノイズの多い日次海運データ向けに作り直したもの。
//
// 設計上の要点（実データ観測に基づく）:
//  - 日次の貿易量/通航数は塊状（バルク船の納入で 0 の日と巨大な日が混在）で極めてlumpy。
//    → 単日や数日では判定せず、RECENT_WINDOW 日（≒1か月）平均で平滑化する。
//  - PortWatch は週次更新で、最新数日は未確定（0 に落ちる）ことがある。
//    → 末尾 TAIL_SKIP 日を「現在水準」の計算から除外する。
//  - σ は「直近1か月の水準が、過去1年の月次水準の分布からどれだけ外れているか」で測る。
//    生の日次ではなく 28日移動平均の分布に対して評価することで安定する。

export const THRESHOLDS = {
  // sigma: 水準の異常（主シグナル）。pct: 前月比の速度（補助シグナル）。
  // sigmaGate: pct での昇格に最低限要求する σ（低トラフィック地点で前月が
  //   たまたま小さく変化率が過大に出る誤検知を抑えるためのゲート）。
  warn: { sigma: 1.5, pct: 30, sigmaGate: 0.75 },
  danger: { sigma: 2.5, pct: 50, sigmaGate: 1.0 },
};

const RECENT_WINDOW = 28; // 現在水準とみなす日数（≒1か月、日次ノイズを平滑化）
const TAIL_SKIP = 2; // 末尾の未確定日を除外する日数
const BASELINE_WINDOW = 365; // σ の基準にする期間（約1年）
const MIN_POINTS = 70; // これ未満（2窓+末尾に満たない）は判定不能とする

const SEVERITY_RANK = { ok: 0, warn: 1, danger: 2 };

function mean(values) {
  if (!values.length) return 0;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

function std(values) {
  if (values.length < 2) return 0;
  const m = mean(values);
  return Math.sqrt(mean(values.map((v) => (v - m) ** 2)));
}

function round(value, digits = 2) {
  const f = 10 ** digits;
  return Math.round(value * f) / f;
}

/** 末尾 window 日ずつの移動平均系列を返す（日次ノイズを均し、σ の母集団にする）。 */
function rollingMean(values, window) {
  const out = [];
  for (let i = window - 1; i < values.length; i += 1) {
    out.push(mean(values.slice(i - window + 1, i + 1)));
  }
  return out;
}

/**
 * 1指標の時系列を解析する。
 * @param {Array<{date:string, value:number|null}>} series 昇順（古い→新しい）
 * @returns 統計量、または点数不足なら null
 */
export function analyzeSeries(series) {
  const clean = series.filter(
    (d) => d && d.value != null && Number.isFinite(d.value),
  );
  if (clean.length < MIN_POINTS) return null;

  const values = clean.map((d) => d.value);
  // 末尾の未確定日を除いた「使える」系列。
  const usable = TAIL_SKIP > 0 ? values.slice(0, -TAIL_SKIP) : values;
  if (usable.length < RECENT_WINDOW * 2) return null;

  // 現在水準（直近1か月平均）と、その1つ前の1か月平均。
  const current = mean(usable.slice(-RECENT_WINDOW));
  const prior = mean(usable.slice(-2 * RECENT_WINDOW, -RECENT_WINDOW));
  const pctChange = prior ? ((current - prior) / Math.abs(prior)) * 100 : 0;

  // σ: 直近1年分の 28日移動平均の分布に対して、現在水準がどれだけ外れているか。
  const smoothed = rollingMean(usable.slice(-BASELINE_WINDOW), RECENT_WINDOW);
  const m = mean(smoothed);
  const s = std(smoothed);
  const sigma = s ? (current - m) / s : 0;

  const latest = clean[clean.length - 1];
  return {
    latest: latest.value,
    latestDate: latest.date,
    current: round(current),
    baseline: round(prior),
    mean: round(m),
    std: round(s),
    sigma: round(sigma),
    pctChange: round(pctChange, 1),
    points: clean.length,
  };
}

/**
 * 統計量を ok / warn / danger に分類する。
 * σ（水準の異常）が主シグナル。変化率（速度）は σ がゲート値以上ある時だけ昇格に使う。
 */
export function classify(stat) {
  if (!stat) return "ok";
  const az = Math.abs(stat.sigma);
  const ap = Math.abs(stat.pctChange);

  const d = THRESHOLDS.danger;
  if (az >= d.sigma || (ap >= d.pct && az >= d.sigmaGate)) return "danger";

  const w = THRESHOLDS.warn;
  if (az >= w.sigma || (ap >= w.pct && az >= w.sigmaGate)) return "warn";

  return "ok";
}

/** 複数シグナルの最悪値を返す（地点の総合シグナル算出に使う）。 */
export function worst(signals) {
  return signals.reduce(
    (acc, sig) => (SEVERITY_RANK[sig] > SEVERITY_RANK[acc] ? sig : acc),
    "ok",
  );
}

export function severityRank(signal) {
  return SEVERITY_RANK[signal] ?? 0;
}

/** アラート表示用の理由文を組み立てる（例: 「通航数 前月比 -34% / -2.8σ」）。 */
export function buildReason(label, stat) {
  const sign = stat.pctChange >= 0 ? "+" : "";
  return `${label} 前月比 ${sign}${stat.pctChange}% / ${stat.sigma}σ`;
}
